import { and, or, not } from 'tio-ui/utilities';
import Button from 'tio-ui/components/button';
import { cached } from '@glimmer/tracking';
import Checkbox from 'tio-ui/components/checkbox';
import Input from 'tio-ui/components/input';
import Textarea from 'tio-ui/components/textarea';
import Popover from 'tio-ui/components/popover';
import { dropTask } from 'ember-concurrency';
import { htmlSafe } from '@ember/template';
import { on } from '@ember/modifier';
import { fn, hash, concat } from '@ember/helper';
import Modal from 'tio-ui/components/modal';
import Footer from 'tio-ui/components/footer';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { VStack } from 'tio-ui/components/layout';
import Component from '@glimmer/component';
import type { TOC } from '@ember/component/template-only';
import { doEmployeesExistInApproverList } from '../../../utils/tuition-assistance/approvers.ts';
import { formatAsDollars } from '../../../utils/format.ts';
import inputmask from '../../../modifiers/inputmask.ts';
import { getInputmaskUnmaskedValue } from '../../../utils/inputmask.ts';
import LimitsSummary from '../../tuition-assistance/limits-summary.gts';
import SelectableCard from 'tio-ui/components/selectable-card';
import ErrorMessages, { type ErrorsSignature } from 'tio-ui/components/error-messages';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import type {
  AppHistoryBase,
  PaymentEvent,
  AssistanceAppHistory,
} from '../../../types/tuition-assistance.ts';
import type Intl from 'ember-intl/services/intl';
// This is for types. Anything we have custom may show an error.
import type { Store } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type TuitionAssistanceService from '../../../services/tuition-assistance.ts';
import {
  PhDotsThreeVertical,
  PhGift,
  PhNotePencil,
  PhShieldCheck,
  PhTrash,
  PhHourglass,
  PhPackage,
  PhCheckCircle,
  PhHandCoins,
} from 'ember-phosphor-icons';

import { updateTasAssistanceAmountRequest } from 'tio-data/requests/tas-assistance';
import { holdTasParticipantRequest } from 'tio-data/requests/tas-participant-actions';
import {
  cancelRepaymentRequest,
  loadTasApplicationHistory,
  requestPaymentRequest,
  requestRepaymentRequest,
  tasMoneyTransactionRequest,
  type TasMoneyTransactionMethod,
} from 'tio-data/requests/tas-application-actions';
import type SessionContextService from '../../../services/session-context.ts';
import { reloadRecord } from 'tio-data/requests/save-record';
interface FinancialDetailsRequestSummarySig {
  Args: {
    application: TasApplicationModel;
    isProhibitedEdit?: boolean;
  };
  Element: HTMLElement;
}

interface FormattedHistoryEvent {
  id: string;
  eventType: string;
  eventLabel: string;
  amount: number;
  formattedAmount: string;
  actionTakenBy: string | null;
  date: Date | string;
  formattedDate: string;
  formattedTime: string;
  canEdit: boolean;
  assistanceId?: string;
  record?: PaymentEvent | AppHistoryBase;
  to?: string;
  logType?: string;
  comment?: string;
}

// Types for PENDING/COMPLETED sections
interface PendingItem {
  id: string;
  type: 'approved_total_pending_payment' | 'assistance';
  assistanceId?: string;
  state?: string;
  manner?: string;
  amount: number;
  formattedAmount: string;
  date: string;
  formattedDate: string;
  formattedTime: string;
  approverName?: string;
  approverComment?: string;
  assistanceType?: string;
  canEdit: boolean;
  record?: PaymentEvent | AppHistoryBase;
  to?: string;
}

interface CompletedItem {
  id: string;
  type: 'assistance';
  assistanceId: string;
  state: string;
  manner?: string;
  amount: number;
  formattedAmount: string;
  date: string;
  formattedDate: string;
  formattedTime: string;
  approverName?: string;
  approverComment?: string;
  assistanceType: string;
}

// Types for REPAYMENT section
interface RepaymentItem {
  id: string;
  type: 'assistance';
  assistanceId: string;
  state: string;
  /** The repayment status: 'requested' | 'received' | 'forgiven' */
  repaymentStatus: 'requested' | 'received' | 'forgiven';
  amount: number;
  formattedAmount: string;
  date: string;
  formattedDate: string;
  formattedTime: string;
  approverName?: string;
  approverComment?: string;
  assistanceType?: string;
  canEdit: boolean;
  record?: PaymentEvent | AppHistoryBase;
  to?: string;
}

const actionMap: Record<string, string> = {
  requestRepayment: 'requestRepayment',
  payMore: 'requestPayment',
  editApprovedTotal: 'updateApprovedTotal',
  forgive: 'forgiveRepayment',
  settle: 'recordRepayment',
  delete: 'cancelRepayment',
};

const eventLabelMap: Record<string, string> = {
  // Application History
  approval: 'Approved Total',

  // Payment Events
  'TAS.AssistanceState.PAYABLE': 'Payment Requested',
  'TAS.AssistanceState.PENDING': 'Payment Pending',
  'TAS.AssistanceState.PAID': 'Payment Approved',

  // Repayment Events
  'TAS.AssistanceState.REPAYABLE': 'Repayment Requested',
  'TAS.AssistanceState.FORGIVEN': 'Repayment Forgiven',
  'TAS.AssistanceState.SETTLED': 'Repayment Settled',

  // Course Cash Events
  CARD_RESERVATION_CREATED: 'Card Reservation Created',
  CARD_RESERVATION_AMOUNT_UPDATED: 'Card Reservation Updated',
  CARD_AUTHORIZED: 'Card Payment Authorized',
  CARD_CAPTURED: 'Card Payment Captured',
  CARD_EXPIRED: 'Card Authorization Expired',
  CARD_REFUNDED: 'Card Payment Refunded',

  // Additional assistance types
  REPAYMENT_REQUEST: 'Repayment Requested',
  REPAYMENT: 'Repayment Recorded',
  REPAYMENT_FORGIVEN: 'Repayment Forgiven',
  REPAYMENT_CANCELLED: 'Repayment Cancelled',
  REPAYMENT_AMOUNT_UPDATED: 'Repayment Amount Updated',
  PAYMENT: 'Payment Approved',
  PAYMENT_PENDING: 'Payment Pending',
  PAYMENT_AMOUNT_UPDATED: 'Payment Amount Updated',
};

export default class FinancialDetailsRequestSummary extends Component<FinancialDetailsRequestSummarySig> {
  @tracked submitError: string | ErrorsSignature | null = null;
  @tracked isAddEventOpen = false;
  @tracked reasonForNewEvent = '';
  @tracked newEventAmount = 0;
  @tracked newEventAmountDisplay = '';
  @tracked newEventType = '';
  @tracked didSubmitNewRequest = false;
  @tracked pauseEmployee = false;
  @tracked sortDescending = true; // Default: newest first
  @tracked repaymentAmountSelection: 'full' | 'reimbursement' | 'courseCash' | 'custom' = 'full';
  @tracked expandedComments = new Set<string>();

  // Shared edit modal state
  @tracked editingEvent: FormattedHistoryEvent | null = null;
  @tracked isEditModalOpen = false;
  @tracked isCancelModalOpen = false;
  @tracked reasonForEdit = '';
  @tracked editableAmount = 0;
  @tracked editableAmountDisplay = '';
  @tracked assistanceAction = '';
  @tracked didSubmitEditRequest = false;

  @service declare store: Store;
  @service declare intl: Intl;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare sessionContext: SessionContextService;

  @cached
  get filteredAssistanceHistory() {
    return this.args.application.assistanceHistory.map((assistance) => {
      const hasCaptured = assistance.records?.some(
        (record) => record.assistanceType === 'CARD_CAPTURED'
      );

      const filteredRecords = hasCaptured
        ? assistance.records?.filter((record) => record.assistanceType !== 'CARD_AUTHORIZED')
        : assistance.records;

      return {
        ...assistance,
        records: filteredRecords,
      };
    });
  }

  // ========================================
  // PENDING / COMPLETED / REPAYMENT Section Logic
  // ========================================

  /**
   * Pending states that indicate a payment action is awaiting processing
   * Note: REPAYABLE is handled separately in the Repayment section
   */
  private readonly pendingStates = new Set([
    'TAS.AssistanceState.PAYABLE',
    'TAS.AssistanceState.AUTHORIZED',
    'TAS.AssistanceState.RESERVED',
  ]);

  /**
   * Repayment-related states
   */
  private readonly repaymentStates = new Set([
    'TAS.AssistanceState.REPAYABLE', // Repayment requested (pending)
    'TAS.AssistanceState.FORGIVEN', // Repayment forgiven (terminal)
  ]);

  /**
   * Assistance types that indicate repayment-related activity
   */
  private readonly repaymentAssistanceTypes = new Set([
    'REPAYMENT_REQUEST',
    'REPAYMENT',
    'REPAYMENT_FORGIVEN',
    'REPAYMENT_CANCELLED',
    'REPAYMENT_AMOUNT_UPDATED',
  ]);

  /**
   * Terminal states for PAYMENT completion (not repayment)
   * Note: SETTLED can come from PAYABLE (payment) or REPAYABLE (repayment)
   * We check the 'from' field to distinguish. FORGIVEN is repayment-only.
   */
  private readonly paymentTerminalStates = new Set([
    'TAS.AssistanceState.SETTLED',
    'TAS.AssistanceState.CAPTURED',
  ]);

  /**
   * Assistance types that represent terminal/completed PAYMENT actions
   * Note: REPAYMENT types are handled in Repayment section
   */
  private readonly paymentTerminalAssistanceTypes = new Set(['PAYMENT', 'CARD_CAPTURED']);

  /**
   * Get items that are pending action (payments awaiting, repayments requested, etc.)
   */
  @cached
  get pendingItems(): PendingItem[] {
    const pending: PendingItem[] = [];
    const assistanceHistory = this.filteredAssistanceHistory;
    const appHistory = this.args.application.approvedTotalHistory || [];

    // CASE 1: No assistance_history yet - check for approved total
    // This happens after first approval but before payment is triggered
    if (!assistanceHistory?.length) {
      const approvalEvents = appHistory.filter((e) => e.logType === 'approval' && e.from !== e.to);

      if (approvalEvents.length > 0) {
        const lastApproval = approvalEvents.at(-1);
        if (lastApproval) {
          const amount = typeof lastApproval.to === 'number' ? lastApproval.to : 0;
          pending.push({
            id: `pending-approval-${lastApproval.date}`,
            type: 'approved_total_pending_payment',
            amount,
            formattedAmount: formatAsDollars(amount),
            date: lastApproval.date,
            formattedDate: this.formatEventDate(lastApproval.date),
            formattedTime: this.formatEventTime(lastApproval.date),
            approverName: lastApproval.approverName ?? undefined,
            canEdit: this.canEditApprovedTotal(appHistory.length - 1, appHistory.length - 1),
            record: lastApproval,
            to: String(lastApproval.to),
          });
        }
      }
      return pending;
    }

    // CASE 2: Check assistance_history for pending groups
    for (const group of assistanceHistory) {
      if (!this.pendingStates.has(group.state)) {
        continue;
      }

      // For AUTHORIZED state, use CARD_AUTHORIZED record (not CARD_RESERVATION_AMOUNT_UPDATED)
      // For other states, use the most recent record
      let relevantRecord = group.records?.at(-1);
      if (group.state === 'TAS.AssistanceState.AUTHORIZED') {
        const authorizedRecord = group.records?.find((r) => r.assistanceType === 'CARD_AUTHORIZED');
        if (authorizedRecord) {
          relevantRecord = authorizedRecord;
        }
      }
      if (!relevantRecord) continue;

      const amount = relevantRecord.amount || 0;
      const manner =
        (relevantRecord as PaymentEvent).manner ?? (relevantRecord as PaymentEvent).assistanceType;

      pending.push({
        id: `pending-${group.assistance_id}-${relevantRecord.date}`,
        type: 'assistance',
        assistanceId: group.assistance_id,
        state: group.state,
        manner,
        amount,
        formattedAmount: this.formatEventAmount(amount, relevantRecord.assistanceType),
        date: relevantRecord.date,
        formattedDate: this.formatEventDate(relevantRecord.date),
        formattedTime: this.formatEventTime(relevantRecord.date),
        approverName: relevantRecord.approverName ?? undefined,
        approverComment: relevantRecord.approverComment ?? undefined,
        assistanceType: relevantRecord.assistanceType,
        canEdit: this.canEditAssistanceGroup(group),
        record: relevantRecord,
        to: relevantRecord.to,
      });
    }

    return pending;
  }

  /**
   * Get items that have reached a terminal PAYMENT state (completed payments only)
   */
  @cached
  get completedItems(): CompletedItem[] {
    const completed: CompletedItem[] = [];
    const assistanceHistory = this.filteredAssistanceHistory;

    for (const group of assistanceHistory ?? []) {
      if (!this.paymentTerminalStates.has(group.state)) {
        continue;
      }

      const records = group.records ?? [];
      const terminalRecords = records.filter(
        (r) =>
          this.paymentTerminalAssistanceTypes.has(r.assistanceType) &&
          (this.paymentTerminalStates.has(r.to) || r.to === group.state)
      );

      if (!terminalRecords.length) {
        continue;
      }

      for (const terminalRecord of terminalRecords) {
        if (
          group.state === 'TAS.AssistanceState.SETTLED' &&
          terminalRecord.from === 'TAS.AssistanceState.REPAYABLE'
        ) {
          continue;
        }

        const amount = terminalRecord.amount || 0;
        const manner = (terminalRecord as PaymentEvent).manner;

        completed.push({
          id: `completed-${group.assistance_id}-${terminalRecord.date}`,
          type: 'assistance',
          assistanceId: group.assistance_id,
          state: group.state,
          manner,
          amount,
          formattedAmount: this.formatEventAmount(Math.abs(amount), terminalRecord.assistanceType),
          date: terminalRecord.date,
          formattedDate: this.formatEventDate(terminalRecord.date),
          formattedTime: this.formatEventTime(terminalRecord.date),
          approverName: terminalRecord.approverName ?? undefined,
          approverComment: terminalRecord.approverComment ?? undefined,
          assistanceType: terminalRecord.assistanceType,
        });
      }
    }

    // Sort by date descending (most recent first)
    return completed.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }

  /**
   * Get items that represent repayment activity
   */
  @cached
  get repaymentItems(): RepaymentItem[] {
    const repaymentItems: RepaymentItem[] = [];
    const assistanceHistory = this.filteredAssistanceHistory;

    for (const group of assistanceHistory ?? []) {
      const record = this.getRepaymentRecord(group);
      if (!record) {
        continue;
      }

      let repaymentStatus: RepaymentItem['repaymentStatus'] | null = null;
      if (group.state === 'TAS.AssistanceState.REPAYABLE') {
        repaymentStatus = 'requested';
      } else if (group.state === 'TAS.AssistanceState.SETTLED') {
        repaymentStatus = 'received';
      } else if (group.state === 'TAS.AssistanceState.FORGIVEN') {
        repaymentStatus = 'forgiven';
      }

      if (!repaymentStatus) {
        continue;
      }

      const amount = record.amount || 0;

      repaymentItems.push({
        id: `repayment-${group.assistance_id}-${record.date}`,
        type: 'assistance',
        assistanceId: group.assistance_id,
        state: group.state,
        repaymentStatus,
        amount,
        formattedAmount: this.formatEventAmount(Math.abs(amount), record.assistanceType),
        date: record.date,
        formattedDate: this.formatEventDate(record.date),
        formattedTime: this.formatEventTime(record.date),
        approverName: record.approverName ?? undefined,
        approverComment: record.approverComment ?? undefined,
        assistanceType: record.assistanceType,
        canEdit: repaymentStatus === 'requested' && this.canEditRepaymentGroup(group),
        record,
        to: record.to,
      });
    }

    return repaymentItems.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }

  /**
   * Helper to check if approved total can be edited
   */
  private canEditApprovedTotal = (index: number, lastIndex: number) => {
    if (this.args.isProhibitedEdit) {
      return false;
    }
    const amounts = this.args.application.amounts;
    return (
      !amounts.flags.request_payment_enabled &&
      !amounts.flags.request_repayment_enabled &&
      amounts.pending_payment === 0 &&
      amounts.paid === 0 &&
      index === lastIndex
    );
  };

  /**
   * Helper to check if assistance group can be edited
   */
  private canEditAssistanceGroup = (assistance: AssistanceAppHistory) => {
    if (this.args.isProhibitedEdit) {
      return false;
    }
    const editableStates = new Set(['TAS.AssistanceState.PAYABLE', 'TAS.AssistanceState.RESERVED']);
    return editableStates.has(assistance.state);
  };

  private canEditRepaymentGroup = (assistance: AssistanceAppHistory) => {
    if (this.args.isProhibitedEdit) {
      return false;
    }

    return assistance.state === 'TAS.AssistanceState.REPAYABLE';
  };

  private getRepaymentRecord = (assistance: AssistanceAppHistory) => {
    if (!this.isRepaymentGroup(assistance)) {
      return null;
    }

    const records = assistance.records ?? [];
    for (let index = records.length - 1; index >= 0; index -= 1) {
      const record = records[index];
      if (!record) {
        continue;
      }
      if (this.isRepaymentRecordForGroup(assistance, record)) {
        return record;
      }
    }

    return null;
  };

  private isRepaymentGroup = (assistance: AssistanceAppHistory) => {
    return (
      this.repaymentStates.has(assistance.state) ||
      assistance.state === 'TAS.AssistanceState.SETTLED'
    );
  };

  private isRepaymentRecordForGroup = (
    assistance: AssistanceAppHistory,
    record: PaymentEvent | undefined
  ) => {
    if (!record) {
      return false;
    }

    const assistanceType = record.assistanceType;

    if (assistance.state === 'TAS.AssistanceState.REPAYABLE') {
      return (
        record.to === 'TAS.AssistanceState.REPAYABLE' ||
        (assistanceType ? this.repaymentAssistanceTypes.has(assistanceType) : false)
      );
    }

    if (assistance.state === 'TAS.AssistanceState.FORGIVEN') {
      return (
        record.to === 'TAS.AssistanceState.FORGIVEN' || assistanceType === 'REPAYMENT_FORGIVEN'
      );
    }

    if (assistance.state === 'TAS.AssistanceState.SETTLED') {
      return (
        record.to === 'TAS.AssistanceState.SETTLED' &&
        (record.from === 'TAS.AssistanceState.REPAYABLE' || assistanceType === 'REPAYMENT')
      );
    }

    return false;
  };

  /**
   * Get label for pending item based on state
   */
  getPendingItemLabel = (item: PendingItem) => {
    if (item.type === 'approved_total_pending_payment') {
      return this.intl.t('common.tas.approval.request_summary.pending.approved_total');
    }

    switch (item.state) {
      case 'TAS.AssistanceState.PAYABLE':
        return this.intl.t('common.tas.approval.request_summary.pending.payment_pending');
      case 'TAS.AssistanceState.AUTHORIZED':
        return this.intl.t('common.tas.approval.request_summary.pending.card_authorized');
      case 'TAS.AssistanceState.RESERVED':
        return this.intl.t('common.tas.approval.request_summary.pending.card_reserved');
      default:
        return this.intl.t('common.tas.approval.request_summary.pending.pending');
    }
  };

  /**
   * Get label for completed item based on assistance type
   */
  getCompletedItemLabel = (item: CompletedItem) => {
    if (item.manner === 'TAS.AssistanceManner.COURSE_CASH') {
      return this.intl.t('common.tas.approval.request_summary.completed.used');
    }
    switch (item.assistanceType) {
      case 'PAYMENT':
      case 'CARD_CAPTURED':
        return this.intl.t('common.tas.approval.request_summary.completed.payment');
      default:
        return this.intl.t('common.tas.approval.request_summary.completed.completed');
    }
  };

  getRepaymentItemLabel = (item: RepaymentItem) => {
    switch (item.repaymentStatus) {
      case 'requested':
        return this.intl.t('common.tas.approval.request_summary.repayment.requested');
      case 'received':
        return this.intl.t('common.tas.approval.request_summary.repayment.received');
      case 'forgiven':
        return this.intl.t('common.tas.approval.request_summary.repayment.forgiven');
      default:
        return this.intl.t('common.tas.approval.request_summary.repayment.requested');
    }
  };

  get hasRepaymentItems() {
    return this.repaymentItems.length > 0;
  }

  get formattedHistoryEvents(): FormattedHistoryEvent[] {
    const events: FormattedHistoryEvent[] = [];

    // Process application history (approved totals)
    const appHistory = this.args.application.approvedTotalHistory || [];
    let approvalEventCount = 0; // Track count of actual approval events added

    appHistory.forEach((record, index) => {
      const eventType = record.logType || 'approval';
      const to = record.to || '';

      // Skip approval events where from === to (no actual change)
      if (record.logType === 'approval' && record.from === record.to) {
        return;
      }

      // For approval events, amount is in the 'to' field (new approved total)
      // For state transitions, amount is in the 'amount' field
      const displayAmount =
        record.logType === 'approval'
          ? typeof record.to === 'number'
            ? record.to
            : 0
          : record.amount || 0;

      events.push({
        id: `app-${index}-${record.date}`,
        eventType,
        eventLabel: this.formatEventType(record, approvalEventCount),
        amount: displayAmount,
        formattedAmount: this.formatEventAmount(displayAmount, eventType),
        actionTakenBy: record.approverName || null,
        date: record.date,
        formattedDate: this.formatEventDate(record.date),
        formattedTime: this.formatEventTime(record.date),
        canEdit: this.canEditEvent(record, index, 'approval', appHistory.length - 1),
        record,
        to,
        logType: 'approval',
        comment: record.approverComment,
      });

      // Increment count only for approval events
      if (record.logType === 'approval') {
        approvalEventCount++;
      }
    });

    // Process assistance history (payments, repayments, course cash)
    // Backend already provides records in correct order (sorted by date)
    const assistanceHistory = this.filteredAssistanceHistory;
    assistanceHistory.forEach((assistance) => {
      const assistanceId = assistance.assistance_id;
      const records = assistance.records || [];

      records.forEach((record, index) => {
        const eventType = record.assistanceType || record.to || '';
        const to = record.to || '';

        events.push({
          id: `assistance-${assistanceId}-${index}-${record.date}`,
          eventType,
          eventLabel: this.formatEventType(record),
          amount: record.amount || 0,
          formattedAmount: this.formatEventAmount(record.amount || 0, eventType),
          actionTakenBy: record.approverName || null,
          date: record.date,
          formattedDate: this.formatEventDate(record.date),
          formattedTime: this.formatEventTime(record.date),
          canEdit: this.canEditEvent(record, index, 'assistance', records.length - 1, assistance),
          assistanceId,
          record,
          to,
          comment: record.approverComment,
        });
      });
    });

    // Only sort if user has toggled sort order (backend provides correct default order)
    if (!this.sortDescending) {
      return events.reverse();
    }
    return events;
  }

  toggleSortOrder = () => {
    this.sortDescending = !this.sortDescending;
  };

  toggleCommentExpanded = (eventId: string) => {
    const newSet = new Set(this.expandedComments);
    if (newSet.has(eventId)) {
      newSet.delete(eventId);
    } else {
      newSet.add(eventId);
    }
    this.expandedComments = newSet;
  };

  isCommentExpanded = (eventId: string) => {
    return this.expandedComments.has(eventId);
  };

  formatEventType = (event: AppHistoryBase | PaymentEvent, index?: number) => {
    const assistanceType = (event as PaymentEvent).assistanceType;
    const to = event.to;
    const logType = (event as AppHistoryBase).logType;

    // For approval events, differentiate between first (index 0) and subsequent updates
    if (logType === 'approval' && typeof index === 'number') {
      if (index === 0) {
        return 'Approved Total';
      } else {
        return 'Approved Total Updated';
      }
    }

    // Try assistanceType first
    if (assistanceType && eventLabelMap[assistanceType]) {
      return eventLabelMap[assistanceType];
    }

    // Try 'to' state
    if (to && eventLabelMap[to]) {
      return eventLabelMap[to];
    }

    // Try logType
    if (logType && eventLabelMap[logType]) {
      return eventLabelMap[logType];
    }

    return 'Event';
  };

  formatEventDate = (dateValue: Date | string) => {
    try {
      const date = new Date(dateValue);
      return date.toLocaleDateString('en-US', {
        year: '2-digit',
        month: 'numeric',
        day: 'numeric',
      });
    } catch {
      return String(dateValue);
    }
  };

  formatEventTime = (dateValue: Date | string) => {
    try {
      const date = new Date(dateValue);
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    } catch {
      return '';
    }
  };

  formatEventAmount = (amount: number, eventType?: string) => {
    // For cancelled repayments, display as positive amount since the repayment is no longer owed
    if (eventType === 'REPAYMENT_CANCELLED') {
      return formatAsDollars(Math.abs(amount));
    }
    return formatAsDollars(amount);
  };

  canEditEvent = (
    record: AppHistoryBase | PaymentEvent,
    index: number,
    historyType: 'approval' | 'assistance',
    lastIndex: number,
    assistance?: AssistanceAppHistory
  ) => {
    if (this.args.isProhibitedEdit) {
      return false;
    }

    if (historyType === 'approval') {
      // Can edit approved total if it's the last one and no payments have been made
      const amounts = this.args.application.amounts;
      return (
        !amounts.flags.request_payment_enabled &&
        !amounts.flags.request_repayment_enabled &&
        amounts.pending_payment === 0 &&
        amounts.paid === 0 &&
        index === lastIndex
      );
    } else {
      // Can edit assistance if it's the last record AND the record's 'to' state is editable
      if (!assistance || index !== lastIndex) return false;

      const recordToState = record.to;
      return (
        recordToState === 'TAS.AssistanceState.PAYABLE' ||
        recordToState === 'TAS.AssistanceState.REPAYABLE' ||
        recordToState === 'TAS.AssistanceState.RESERVED'
      );
    }
  };

  /**
   * Check if the current user is a valid TAS approver for this application
   * (i.e., they are in the list of possible approvers for the workflow)
   */
  get isValidTasApprover() {
    const allPossibleApproverEmployeeIds =
      this.args.application.allPossibleApproverEmployeeIdsForAllWorkflows;
    const employees = this.sessionContext.user?.person?.employees || [];

    return doEmployeesExistInApproverList(employees, allPossibleApproverEmployeeIds);
  }

  get hasAddEventPermission() {
    // Only specific roles can request payment or repayment:
    // TIO_APPROVER, ACCOUNT_OWNER, EMPLOYEE_ADMIN, TAS_APPROVER
    const currentUser = this.sessionContext.user;

    return (
      !!this.isValidTasApprover ||
      !!currentUser?.isEmployeeAdmin ||
      !!currentUser?.isAccountOwner ||
      !!currentUser?.isTioAdmin ||
      !!currentUser?.isTasTioApprover
    );
  }

  get showAddEventButton() {
    if (!this.hasAddEventPermission) {
      return false;
    }

    return (
      this.args.application.amounts?.flags.request_payment_enabled ||
      this.args.application.amounts?.flags.request_repayment_enabled
    );
  }

  get showPayMoreButton() {
    return (
      this.hasAddEventPermission &&
      this.args.application.amounts?.flags.request_payment_enabled &&
      !this.hasPendingReimbursement
    );
  }

  /**
   * Check if there's a pending reimbursement payment
   * We shouldn't show "Pay More" while a reimbursement is pending
   */
  get hasPendingReimbursement() {
    const assistanceHistory = this.filteredAssistanceHistory;
    if (!assistanceHistory?.length) return false;

    return assistanceHistory.some((group) => {
      if (group.state !== 'TAS.AssistanceState.PAYABLE') return false;

      const lastRecord = group.records?.at(-1);
      if (!lastRecord) return false;

      // Check if it's a reimbursement (not course cash)
      const manner =
        (lastRecord as { manner?: string }).manner ??
        (lastRecord as { assistanceType?: string }).assistanceType;
      return manner !== 'TAS.AssistanceManner.COURSE_CASH';
    });
  }

  get showRequestRepaymentButton() {
    return (
      this.hasAddEventPermission && this.args.application.amounts?.flags.request_repayment_enabled
    );
  }

  get isCourseCashApplication() {
    return this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
  }

  get repaymentAmountOptions() {
    if (!this.isCourseCashApplication) {
      return null;
    }

    const amounts = this.args.application.amounts;
    const options = [
      {
        key: 'full',
        label: 'Full Amount',
        description: 'Total amount available for repayment',
        amount: Math.abs(amounts.refund_limit ?? 0),
      },
      {
        key: 'reimbursement',
        label: 'Reimbursement Only',
        description: 'Paid via direct payments',
        amount: Math.abs(amounts.paid ?? 0),
      },
      {
        key: 'courseCash',
        label: 'Course Cash Only',
        description: 'Spent via card',
        amount: Math.abs(amounts.course_cash?.used ?? 0),
      },
    ];

    // Only show options with amounts > 0
    return options.filter((option) => option.amount > 0);
  }

  openAddEventModal = () => {
    this.isAddEventOpen = true;
  };

  openPayMoreModal = () => {
    this.newEventType = 'payMore';
    this.newEventAmount = 0;
    this.newEventAmountDisplay = '0';
    this.isAddEventOpen = true;
  };

  openRequestRepaymentModal = () => {
    this.newEventType = 'requestRepayment';
    // Pre-fill amount and pause settings for repayment requests
    this.newEventAmount = Math.abs(this.args.application.amounts.refund_limit) || 0;
    this.newEventAmountDisplay = String(this.newEventAmount / 100);
    this.pauseEmployee = true;
    this.isAddEventOpen = true;
  };

  closeAddEventModal = () => {
    this.isAddEventOpen = false;
    this.resetForm();
  };

  resetForm() {
    this.newEventType = '';
    this.reasonForNewEvent = '';
    this.newEventAmount = 0;
    this.newEventAmountDisplay = '';
    this.pauseEmployee = false;
    this.didSubmitNewRequest = false;
    this.submitError = null;
    this.repaymentAmountSelection = 'full';
  }

  get isAddEventFormValid() {
    return !!(
      this.newEventAmount &&
      this.newEventAmount > 0 &&
      this.reasonForNewEvent &&
      this.newEventType
    );
  }

  setNewEventType = (value: string) => {
    this.newEventType = value;

    // Auto-fill amount and pause settings for repayment requests
    if (value === 'requestRepayment') {
      this.newEventAmount = Math.abs(this.args.application.amounts.refund_limit) || 0;
      this.newEventAmountDisplay = String(this.newEventAmount / 100);
      this.pauseEmployee = true;
      this.repaymentAmountSelection = 'full';
    } else {
      this.newEventAmount = 0;
      this.newEventAmountDisplay = '0';
      this.pauseEmployee = false;
      this.repaymentAmountSelection = 'full';
    }
  };

  selectRepaymentAmount = (key: string) => {
    this.repaymentAmountSelection = key as 'full' | 'reimbursement' | 'courseCash' | 'custom';

    const option = this.repaymentAmountOptions?.find((opt) => opt.key === key);
    if (option) {
      this.newEventAmount = option.amount;
      this.newEventAmountDisplay = String(option.amount / 100);
    }
  };

  updateNewEventAmount = (_maskedValue: unknown, event?: InputEvent) => {
    const unmaskedValue = getInputmaskUnmaskedValue(event);
    let valueInCents = Math.round(Number(unmaskedValue) * 100);

    // For repayment requests, enforce max limit = refund_limit
    if (this.newEventType === 'requestRepayment') {
      const maxAmount = Math.abs(this.args.application.amounts.refund_limit) || 0;
      valueInCents = Math.min(valueInCents, maxAmount);
    }

    this.newEventAmount = valueInCents;

    // Mark as custom if user manually edits.
    // Do NOT update newEventAmountDisplay here — updating @value on every
    // keystroke can cause input remount/focus churn in this modal.
    if (this.repaymentAmountSelection !== 'custom') {
      this.repaymentAmountSelection = 'custom';
    }
  };

  updateReasonForNewEvent = (value: string) => {
    this.reasonForNewEvent = value;
  };

  get tasParticipant() {
    return this.args.application.tasProgramInstance.employee.tasParticipant;
  }

  togglePauseEmployee = () => {
    this.pauseEmployee = !this.pauseEmployee;
  };

  submitNewEventTask = dropTask(async () => {
    this.submitError = null;
    this.didSubmitNewRequest = true;

    if (!this.isAddEventFormValid) {
      return;
    }

    try {
      // WarpDrive mutation builders (Phase 2.7) — replace
      // adapter.requestRepayment(...) / adapter.requestPayment(...) (money ops + reload).
      if (this.newEventType === 'requestRepayment') {
        await this.store.request(
          requestRepaymentRequest(
            this.args.application.id as string,
            this.newEventAmount,
            this.reasonForNewEvent
          )
        );
        await reloadRecord(this.store, this.args.application, {
          include: 'tas-assistances,tas-program-instance.employee.tas-participant',
        });

        // Pause employee after successful repayment request if checkbox was selected
        if (this.pauseEmployee) {
          await this.store.request(holdTasParticipantRequest(this.tasParticipant.id as string));
        }
      } else if (this.newEventType === 'payMore') {
        await this.store.request(
          requestPaymentRequest(
            this.args.application.id as string,
            this.newEventAmount,
            this.reasonForNewEvent
          )
        );
        await reloadRecord(this.store, this.args.application, {
          include: 'tas-assistances,tas-program-instance.employee.tas-participant',
        });
      }

      this.closeAddEventModal();
      this.didSubmitNewRequest = false;
      await loadTasApplicationHistory(this.store, this.args.application);
      this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
    } catch (error) {
      this.submitError = error;
    }
  });

  // Shared edit modal methods
  get adapterMethod() {
    // Possible adapter methods based on assistanceAction (tas-application adapter):
    // - 'editApprovedTotal' → updateApprovedTotal(application, amount, reason)
    // - 'forgive' → forgiveRepayment(application, amount, reason)
    // - 'settle' → recordRepayment(application, amount, reason)
    // Note: 'edit' is handled separately via store.request(updateTasAssistanceAmountRequest(...))
    return actionMap[this.assistanceAction] || '';
  }

  get isSettleOrForgiveAction() {
    return this.assistanceAction === 'settle' || this.assistanceAction === 'forgive';
  }

  get amountDescriptionString() {
    const { assistanceAction, editingEvent } = this;
    const toState = editingEvent?.to ?? '';

    const descriptions: Record<string, string> = {
      settle:
        'Employee is paying back the employer. This can be 100% settlement or partial settlement.',
      forgive: 'Employee is being forgiven from the current repayment amount.',
      editApprovedTotal: 'By changing the amount, you are updating the approved total.',
    };

    if (assistanceAction === 'edit') {
      switch (toState) {
        case 'TAS.AssistanceState.PAYABLE':
          return 'By changing the amount, you are updating the payment amount.';
        case 'TAS.AssistanceState.REPAYABLE':
          return 'By changing the amount, you are updating the repayment amount.';
        case 'TAS.AssistanceState.RESERVED':
          return 'By changing the amount, you are updating the reserved amount for Course Cash.';
        default:
          return '';
      }
    }

    return descriptions[assistanceAction] ?? '';
  }

  get isEditFormValid() {
    // Settle/forgive require a positive amount, but an amount edit may be set to 0
    // (e.g. zeroing a Course Cash reservation). The backend update-amount endpoint
    // accepts 0 for these states.
    const amountValid = this.isSettleOrForgiveAction
      ? this.editableAmount > 0
      : this.editableAmount >= 0;
    return !!this.reasonForEdit && amountValid && !!this.assistanceAction;
  }

  get isDeleteFormValid() {
    return this.reasonForEdit && this.assistanceAction;
  }

  get shouldIgnoreLimitsValidationForAddEvent() {
    return this.newEventType === 'requestRepayment';
  }

  get shouldIgnoreLimitsValidationForEditModal() {
    const isAmountEditAction =
      this.assistanceAction === 'edit' || this.assistanceAction === 'editApprovedTotal';
    if (!isAmountEditAction) {
      return true;
    }

    return this.editingEvent?.to === 'TAS.AssistanceState.REPAYABLE';
  }

  openEditModal = (event: FormattedHistoryEvent, action: string) => {
    this.editingEvent = event;
    this.assistanceAction = action;
    this.editableAmount = Math.abs(event.amount || 0);
    this.editableAmountDisplay = String(Math.abs(event.amount || 0) / 100);
    this.isEditModalOpen = true;
  };

  closeEditModal = () => {
    this.isEditModalOpen = false;
    this.resetEditForm();
  };

  openCancelModal = (event: FormattedHistoryEvent) => {
    this.editingEvent = event;
    this.assistanceAction = 'delete';
    this.isCancelModalOpen = true;
  };

  closeCancelModal = () => {
    this.isCancelModalOpen = false;
    this.resetEditForm();
  };

  resetEditForm() {
    this.editingEvent = null;
    this.assistanceAction = '';
    this.reasonForEdit = '';
    this.editableAmount = 0;
    this.editableAmountDisplay = '';
    this.submitError = null;
    this.didSubmitEditRequest = false;
  }

  updateReasonForEdit = (reason: string) => {
    this.reasonForEdit = reason;
  };

  setAction = (value: string) => {
    this.assistanceAction = value;
  };

  updateEditAmount = (_maskedValue: unknown, event?: InputEvent) => {
    const unmaskedValue = getInputmaskUnmaskedValue(event);
    const amount = Math.round(Number(unmaskedValue) * 100);
    this.editableAmount = amount;
    // Do NOT update editableAmountDisplay here — updating @value on every keystroke
    // causes the input to re-render and the cursor to jump to the end.
  };

  submitEditTask = dropTask(async () => {
    this.submitError = null;
    this.didSubmitEditRequest = true;

    const isEditingAssistance = this.assistanceAction === 'edit';

    try {
      if (isEditingAssistance) {
        const assistanceId = this.editingEvent?.assistanceId;
        if (!assistanceId) {
          this.submitError = 'Missing assistance ID — cannot update amount.';
          return;
        }
        await this.store.request(
          updateTasAssistanceAmountRequest(assistanceId, this.editableAmount, this.reasonForEdit)
        );
      } else {
        // WarpDrive mutation builder (Phase 2.7) — replaces
        // adapter[this.adapterMethod](application, amount, reason) (a money op + reload).
        // adapterMethod resolves to updateApprovedTotal / forgiveRepayment / recordRepayment.
        const method = this.adapterMethod as TasMoneyTransactionMethod;
        await this.store.request(
          tasMoneyTransactionRequest(
            method,
            this.args.application.id as string,
            this.editableAmount,
            this.reasonForEdit
          )
        );
        await reloadRecord(this.store, this.args.application, {
          include:
            method === 'updateApprovedTotal'
              ? 'tas-assistances'
              : 'tas-assistances,tas-program-instance.employee.tas-participant',
        });
      }
      this.closeEditModal();
      this.didSubmitEditRequest = false;
      await loadTasApplicationHistory(this.store, this.args.application);
      await this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
    } catch (e) {
      console.error(e);
      this.submitError = e;
    }
  });

  cancelRepaymentTask = dropTask(async () => {
    this.submitError = null;
    this.didSubmitEditRequest = true;

    try {
      // WarpDrive mutation builder (Phase 2.7) — replaces
      // adapter.cancelRepayment(application, reason) (PATCH /cancel-repayment +
      // reload). cancel sends `{ reason }` only (reason-only wire).
      await this.store.request(
        cancelRepaymentRequest(this.args.application.id as string, this.reasonForEdit)
      );
      await reloadRecord(this.store, this.args.application, {
        include: 'tas-assistances,tas-program-instance.employee.tas-participant',
      });
      this.closeCancelModal();
      this.didSubmitEditRequest = false;
      await loadTasApplicationHistory(this.store, this.args.application);
    } catch (e) {
      console.error(e);
      this.submitError = e;
    }
  });

  <template>
    <VStack class="tio-col-flush w-full h-full" ...attributes>
      <div class="tio-col-extra-loose flex-1">
        {{! ========================================
            PENDING Section
            ======================================== }}
        <section aria-labelledby="pending-section-heading">
          <Heading @level={{4}} class="tio-section-kicker">
            {{t "common.tas.approval.request_summary.sections.pending"}}
          </Heading>

          {{#if this.pendingItems.length}}
            <div class="tio-col" data-test="pending-items">
              {{#each this.pendingItems as |item|}}
                <PendingItemCard
                  @item={{item}}
                  @label={{this.getPendingItemLabel item}}
                  @application={{@application}}
                  @isProhibitedEdit={{@isProhibitedEdit}}
                  @onOpenEditModal={{this.openEditModal}}
                  @onOpenCancelModal={{this.openCancelModal}}
                />
              {{/each}}
            </div>
          {{else}}
            <div
              class="tio-row p-4 tio-surface-sunken border tio-border-default"
              role="status"
              data-test="pending-empty-state"
            >
              <PhHourglass @size="24" class="tio-fg-disabled" />
              <Text @variant="muted" @size="sm">
                {{t "common.tas.approval.request_summary.sections.no_pending"}}
              </Text>
            </div>
          {{/if}}
        </section>

        {{! ========================================
            COMPLETED Section
            ======================================== }}
        <section aria-labelledby="completed-section-heading">
          <Heading @level={{4}} class="tio-section-kicker">
            {{t "common.tas.approval.request_summary.sections.completed"}}
          </Heading>

          {{#if this.completedItems.length}}
            <div class="tio-col" data-test="completed-items">
              {{#each this.completedItems as |item|}}
                <CompletedItemCard @item={{item}} @label={{this.getCompletedItemLabel item}} />
              {{/each}}
            </div>
          {{else}}
            <div
              class="tio-row p-4 tio-surface-sunken border tio-border-default"
              role="status"
              data-test="completed-empty-state"
            >
              <PhPackage @size="24" class="tio-fg-disabled" />
              <Text @variant="muted" @size="sm">
                {{t "common.tas.approval.request_summary.sections.no_completed"}}
              </Text>
            </div>
          {{/if}}
        </section>

        {{! ========================================
            REPAYMENT Section
            ======================================== }}
        {{#if this.hasRepaymentItems}}
          <section aria-labelledby="repayment-section-heading">
            <Heading @level={{4}} class="tio-section-kicker">
              {{t "common.tas.approval.request_summary.sections.repayment"}}
            </Heading>

            <div class="tio-col" data-test="repayment-items">
              {{#each this.repaymentItems as |item|}}
                <RepaymentItemCard
                  @item={{item}}
                  @label={{this.getRepaymentItemLabel item}}
                  @application={{@application}}
                  @isProhibitedEdit={{@isProhibitedEdit}}
                  @onOpenEditModal={{this.openEditModal}}
                  @onOpenCancelModal={{this.openCancelModal}}
                />
              {{/each}}
            </div>
          </section>
        {{/if}}
      </div>

      {{! Footer pinned to bottom via flexbox }}
      {{#if this.showAddEventButton}}
        <div class="pt-4 tio-bg-surface border-t tio-border-default">
          <div class="tio-flex-loose">
            {{#if this.showRequestRepaymentButton}}
              <Button
                @intent="primary"
                @appearance="default"
                class="flex-1"
                {{on "click" this.openRequestRepaymentModal}}
                data-test="request-repayment-button"
              >
                {{t "common.tas.approval.request_summary.request_repayment"}}
              </Button>
            {{/if}}
            {{#if this.showPayMoreButton}}
              <Button
                @intent="primary"
                @appearance="default"
                class="flex-1"
                {{on "click" this.openPayMoreModal}}
                data-test="pay-more-button"
              >
                {{t "common.tas.approval.request_summary.pay_more_label"}}
              </Button>
            {{/if}}
          </div>
        </div>
      {{/if}}
    </VStack>

    {{#if this.isAddEventOpen}}
      <LimitsSummary
        @application={{@application}}
        @employee={{@application.tasProgramInstance.employee}}
        @approvedTotalToValidate={{this.newEventAmount}}
        @ignoreLimitsValidation={{this.shouldIgnoreLimitsValidationForAddEvent}}
        @valueClass="w-full app-status-text-brand"
        as |Summary|
      >
        <Modal
          @isOpen={{this.isAddEventOpen}}
          @onClose={{this.closeAddEventModal}}
          @size="xl"
          data-test-add-event-modal
          @ariaLabel={{t "common.aria_labels.add_financial_event"}}
        >
          <:header>Add Event</:header>
          <:body>
            <div>Select event type:</div>
            <div
              class="tio-flex-loose mt-4 mb-8 text-pretty"
              role="group"
              aria-label="Event type selection"
            >
              <SelectableCard
                @value="requestRepayment"
                @label="Request Repayment"
                @description="The employee must return previously paid funds"
                @isSelected={{eq this.newEventType "requestRepayment"}}
                @onChange={{this.setNewEventType}}
                @disabled={{not @application.amounts.flags.request_repayment_enabled}}
              />
              <SelectableCard
                @value="payMore"
                @label="Pay More"
                @description="The employee is being given extra funds"
                @isSelected={{eq this.newEventType "payMore"}}
                @onChange={{this.setNewEventType}}
                @disabled={{not @application.amounts.flags.request_payment_enabled}}
              />
            </div>

            {{! Course Cash Quick-Select Amount Options }}
            {{#if (and (eq this.newEventType "requestRepayment") this.isCourseCashApplication)}}
              <div class="tio-col mb-6">
                <div class="tio-text-sm-semibold tio-fg-primary">Select Amount to Request:</div>

                {{#each this.repaymentAmountOptions as |option|}}
                  <SelectableCard
                    @value={{option.key}}
                    @label={{concat option.label ": " (formatAsDollars option.amount)}}
                    @description={{option.description}}
                    @isSelected={{eq this.repaymentAmountSelection option.key}}
                    @onChange={{this.selectRepaymentAmount}}
                  />
                {{/each}}

                <div class="text-center tio-fg-disabled text-sm">or enter custom amount below</div>
              </div>
            {{/if}}

            <form class="tio-col-flush" aria-label="Add event form">
              <Input
                @label="Amount*"
                data-test-new-event-amount
                @value={{this.newEventAmountDisplay}}
                @onChange={{this.updateNewEventAmount}}
                @classes={{hash base="text-xs font-semibold"}}
                placeholder="$00.00"
                {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
              />
              <div class="mb-8">
                <p class="text-xs pt-1 px-2 tio-fg-muted">
                  {{#if (eq this.newEventType "payMore")}}
                    Additional amount to be paid to the employee
                  {{else if (eq this.newEventType "requestRepayment")}}
                    Amount to request repayment from the employee
                  {{/if}}
                </p>
                <Summary.LimitsAlertForApproverIfApplicable class="text-sm" />
              </div>

              <Textarea
                data-test-reason-for-new-event
                @value={{this.reasonForNewEvent}}
                @onChange={{this.updateReasonForNewEvent}}
                @label="Specify Reason*"
                @classes={{hash base="text-xs font-semibold"}}
                placeholder="Add a comment"
              />
              {{#if (eq this.newEventType "requestRepayment")}}
                <Checkbox
                  @label="Place employee on hold"
                  @checked={{this.pauseEmployee}}
                  @onChange={{this.togglePauseEmployee}}
                  class="tio-row-flush tio-text-sm-semibold mt-4"
                />
              {{/if}}
              <ErrorMessages @showErrors={{this.didSubmitNewRequest}} @error={{this.submitError}} />
            </form>
          </:body>
          <:footer>
            <Footer
              @isRunning={{this.submitNewEventTask.isRunning}}
              @onCancel={{this.closeAddEventModal}}
              @onSubmit={{this.submitNewEventTask.perform}}
              @submitDisabled={{or
                (not this.isAddEventFormValid)
                Summary.isDisabledSubmitForApprover
              }}
            />
          </:footer>
        </Modal>
      </LimitsSummary>
    {{/if}}

    {{! Shared Edit/Forgive/Settle Modal }}
    {{#if this.isEditModalOpen}}
      <LimitsSummary
        @application={{@application}}
        @employee={{@application.tasProgramInstance.employee}}
        @approvedTotalToValidate={{this.editableAmount}}
        @ignoreLimitsValidation={{this.shouldIgnoreLimitsValidationForEditModal}}
        @labelClass="w-full"
        @valueClass="w-full app-status-text-brand"
        as |Summary|
      >
        <Modal
          @isOpen={{this.isEditModalOpen}}
          @onClose={{this.closeEditModal}}
          @size="xl"
          data-test-edit-modal
          @ariaLabel={{t "common.aria_labels.update_request_details"}}
        >
          <:header>Update Request Details</:header>
          <:body>
            {{#if this.isSettleOrForgiveAction}}
              <div>
                {{t "common.tas.approval.request_summary.select_action"}}
              </div>
              <div class="tio-flex-loose mt-4 mb-8">
                <SelectableCard
                  @value="settle"
                  @label="Mark as Paid"
                  @description="Employee is paying back the employer."
                  @isSelected={{eq this.assistanceAction "settle"}}
                  @onChange={{this.setAction}}
                />
                <SelectableCard
                  @value="forgive"
                  @label={{t "common.tas.approval.request_summary.forgive_label"}}
                  @description="Employee is being forgiven from the repayment amount."
                  @isSelected={{eq this.assistanceAction "forgive"}}
                  @onChange={{this.setAction}}
                />
              </div>
            {{/if}}
            <form class="tio-col-flush" aria-label="Edit event form">
              <Input
                data-test-amount-input
                @value={{this.editableAmountDisplay}}
                @onChange={{this.updateEditAmount}}
                @label="Amount*"
                @classes={{hash base="text-xs font-semibold"}}
                {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
              />
              <div class="mb-8">
                <Summary.LimitsAlertForApproverIfApplicable class="text-sm" />
              </div>
              {{#if this.amountDescriptionString}}
                <p class="text-sm tio-fg-secondary mb-4">{{this.amountDescriptionString}}</p>
              {{/if}}

              <Textarea
                data-test-reason-input
                @value={{this.reasonForEdit}}
                @onChange={{this.updateReasonForEdit}}
                @label="Specify Reason*"
                @classes={{hash base="text-xs font-semibold mt-8"}}
                placeholder="Add a comment"
              />
              <ErrorMessages
                @showErrors={{this.didSubmitEditRequest}}
                @error={{this.submitError}}
              />
            </form>
          </:body>
          <:footer>
            <Footer
              @isRunning={{this.submitEditTask.isRunning}}
              @onCancel={{this.closeEditModal}}
              @onSubmit={{this.submitEditTask.perform}}
              @cancelText="Cancel"
              @submitText="Submit"
              @submitDisabled={{or (not this.isEditFormValid) Summary.isDisabledSubmitForApprover}}
            />
          </:footer>
        </Modal>
      </LimitsSummary>
    {{/if}}

    {{! Shared Cancel/Delete Modal }}
    {{#if this.isCancelModalOpen}}
      <Modal
        @isOpen={{this.isCancelModalOpen}}
        @onClose={{this.closeCancelModal}}
        data-test-delete-modal
        @ariaLabel={{t "common.aria_labels.cancel_repayment_request"}}
      >
        <:header>
          <div class="pr-2 text-pretty">Cancel Repayment Request</div>
        </:header>
        <:body>
          <p>
            This action will cancel the repayment request and affect the reimbursement total.
          </p>

          <Textarea
            data-test-reason-for-delete
            @value={{this.reasonForEdit}}
            @onChange={{this.updateReasonForEdit}}
            @label="Specify Reason*"
            @classes={{hash base="text-xs font-semibold mt-8"}}
            placeholder="Add a comment"
          />
          <ErrorMessages @showErrors={{this.didSubmitEditRequest}} @error={{this.submitError}} />
        </:body>
        <:footer>
          <Footer
            @isRunning={{this.cancelRepaymentTask.isRunning}}
            @onCancel={{this.closeCancelModal}}
            @onSubmit={{this.cancelRepaymentTask.perform}}
            @cancelText="Cancel"
            @submitText="Delete"
            @submitDisabled={{not this.isDeleteFormValid}}
          />
        </:footer>
      </Modal>
    {{/if}}
  </template>
}

// ========================================
// PendingItemCard Component
// ========================================
interface PendingItemCardSig {
  Args: {
    item: PendingItem;
    label: string;
    application: TasApplicationModel;
    isProhibitedEdit?: boolean;
    onOpenEditModal: (event: FormattedHistoryEvent, action: string) => void;
    onOpenCancelModal: (event: FormattedHistoryEvent) => void;
  };
  Element: HTMLDivElement;
}

const PendingItemCard: TOC<PendingItemCardSig> = <template>
  <div
    class="tio-assistance-status-card"
    data-test-pending-item={{@item.id}}
    role="listitem"
    aria-label={{@label}}
  >
    <div class="tio-row-between gap-8">
      {{! Left Frame - Icon and Info }}
      <div class="tio-row-loose tio-fill-min">
        {{! Icon Badge - gray background with icon }}
        <div class="shrink-0 tio-bg-sunken rounded-full p-0.5">
          {{#if (eq @item.type "approved_total_pending_payment")}}
            <PhHourglass @size="20" class="tio-fg-secondary" />
          {{else if (eq @item.state "TAS.AssistanceState.PAYABLE")}}
            <PhHourglass @size="20" class="tio-fg-secondary" />
          {{else if (eq @item.state "TAS.AssistanceState.REPAYABLE")}}
            <PhHourglass @size="20" class="tio-fg-secondary" />
          {{else if
            (or
              (eq @item.state "TAS.AssistanceState.AUTHORIZED")
              (eq @item.state "TAS.AssistanceState.RESERVED")
            )
          }}
            <PhHourglass @size="20" class="tio-fg-secondary" />
          {{else}}
            <PhHourglass @size="20" class="tio-fg-secondary" />
          {{/if}}
        </div>

        {{! Info }}
        <div class="tio-col-tight min-w-0">
          {{! First line: Reimbursement or Course Cash }}
          <span class="tio-text-base-medium tio-fg-primary">
            {{#if (eq @item.manner "TAS.AssistanceManner.COURSE_CASH")}}
              {{htmlSafe (t "common.tas.course_cash.default")}}
            {{else}}
              {{t "common.progress_tracker.reimbursement"}}
            {{/if}}
          </span>

          {{! Metadata Row - Date · Label }}
          <div class="tio-row-extra-tight tio-text-caption font-medium tracking-wide">
            <span>{{@item.formattedDate}}</span>
            <span class="tio-fg-disabled">·</span>
            <span>{{@label}}</span>
          </div>
        </div>
      </div>

      {{! Right Frame - Amount & Actions }}
      <div class="tio-row-tight">
        <span class="tio-text-base-semibold tio-fg-muted tabular-nums">
          {{@item.formattedAmount}}
        </span>

        {{! Actions Menu }}
        {{#if @item.canEdit}}
          <PendingItemActionsMenu
            @item={{@item}}
            @application={{@application}}
            @onOpenEditModal={{@onOpenEditModal}}
            @onOpenCancelModal={{@onOpenCancelModal}}
          />
        {{else}}
          <div class="w-5"></div>
        {{/if}}
      </div>
    </div>
  </div>
</template>;

// PendingItemActionsMenu Component
interface PendingItemActionsMenuSig {
  Args: {
    item: PendingItem;
    application: TasApplicationModel;
    onOpenEditModal: (event: FormattedHistoryEvent, action: string) => void;
    onOpenCancelModal: (event: FormattedHistoryEvent) => void;
  };
  Element: HTMLDivElement;
}

class PendingItemActionsMenu extends Component<PendingItemActionsMenuSig> {
  @service declare intl: Intl;

  @tracked isMenuOpen = false;

  toggleMenu = () => {
    this.isMenuOpen = !this.isMenuOpen;
  };

  closeMenu = () => {
    this.isMenuOpen = false;
  };

  /**
   * Convert PendingItem to FormattedHistoryEvent for compatibility with existing edit modals
   */
  get eventForModal(): FormattedHistoryEvent {
    const item = this.args.item;
    return {
      id: item.id,
      eventType: item.assistanceType || 'approval',
      eventLabel: '',
      amount: item.amount,
      formattedAmount: item.formattedAmount,
      actionTakenBy: item.approverName || null,
      date: item.date,
      formattedDate: item.formattedDate,
      formattedTime: item.formattedTime,
      canEdit: item.canEdit,
      assistanceId: item.assistanceId,
      record: item.record,
      to: item.to,
      logType: item.type === 'approved_total_pending_payment' ? 'approval' : 'assistance',
      comment: item.approverComment,
    };
  }

  handleAction = (action: string) => {
    this.isMenuOpen = false;
    let finalAction = action;
    if (action === 'edit' && this.args.item.type === 'approved_total_pending_payment') {
      finalAction = 'editApprovedTotal';
    }

    setTimeout(() => {
      if (finalAction === 'delete') {
        this.args.onOpenCancelModal(this.eventForModal);
      } else {
        this.args.onOpenEditModal(this.eventForModal, finalAction);
      }
    }, 500);
  };

  <template>
    <Popover
      @placement="left-start"
      @isOpen={{this.isMenuOpen}}
      @onClose={{this.closeMenu}}
      @maxWidth={{200}}
    >
      <:trigger as |pop|>
        <Button
          @appearance="minimal"
          @size="sm"
          {{pop.register}}
          {{on "click" this.toggleMenu}}
          data-test-pending-actions={{@item.id}}
        >
          <PhDotsThreeVertical @size="24" />
        </Button>
      </:trigger>
      <:body>
        <div class="tio-col-flush py-1 whitespace-nowrap">
          {{! Edit - for PAYABLE, RESERVED, or approved_total }}
          {{#if
            (or
              (eq @item.type "approved_total_pending_payment")
              (eq @item.state "TAS.AssistanceState.PAYABLE")
              (eq @item.state "TAS.AssistanceState.RESERVED")
            )
          }}
            <button
              type="button"
              class="tio-menu-action tio-menu-action--compact"
              {{on "click" (fn this.handleAction "edit")}}
              data-test-edit={{@item.id}}
            >
              <PhNotePencil @size="16" />
              {{t "common.edit"}}
            </button>
          {{/if}}

          {{! Forgive - only for REPAYABLE }}
          {{#if (eq @item.state "TAS.AssistanceState.REPAYABLE")}}
            <button
              type="button"
              class="tio-menu-action tio-menu-action--compact"
              {{on "click" (fn this.handleAction "edit")}}
              data-test-edit={{@item.id}}
            >
              <PhNotePencil @size="16" />
              {{t "common.tas.approval.request_summary.change_amount"}}
            </button>

            <button
              type="button"
              class="tio-menu-action tio-menu-action--compact"
              {{on "click" (fn this.handleAction "forgive")}}
              data-test-forgive={{@item.id}}
            >
              <PhGift @size="16" />
              {{t "common.tas.approval.request_summary.forgive"}}
            </button>

            <button
              type="button"
              class="tio-menu-action tio-menu-action--compact"
              {{on "click" (fn this.handleAction "settle")}}
              data-test-settle={{@item.id}}
            >
              <PhShieldCheck @size="16" />
              {{t "common.tas.approval.request_summary.mark_as_paid"}}
            </button>

            <hr class="my-1 tio-border-subtle" />

            <button
              type="button"
              class="tio-row-tight w-full px-3 py-2 text-sm tio-fg-danger hover:tio-bg-danger-soft rounded"
              {{on "click" (fn this.handleAction "delete")}}
              data-test-delete={{@item.id}}
            >
              <PhTrash @size="16" />
              {{t "common.tas.approval.request_summary.cancel_repayment"}}
            </button>
          {{/if}}
        </div>
      </:body>
    </Popover>
  </template>
}

// ========================================
// RepaymentItemCard Component
// ========================================
interface RepaymentItemCardSig {
  Args: {
    item: RepaymentItem;
    label: string;
    application: TasApplicationModel;
    isProhibitedEdit?: boolean;
    onOpenEditModal: (event: FormattedHistoryEvent, action: string) => void;
    onOpenCancelModal: (event: FormattedHistoryEvent) => void;
  };
  Element: HTMLDivElement;
}

const RepaymentItemCard: TOC<RepaymentItemCardSig> = <template>
  <div
    class="tio-assistance-status-card"
    data-test-repayment-item={{@item.id}}
    role="listitem"
    aria-label={{@label}}
  >
    <div class="tio-row-between gap-8">
      {{! Left Frame - Icon and Info }}
      <div class="tio-row-loose tio-fill-min">
        <div class="shrink-0 tio-bg-danger-soft rounded-full p-1">
          <PhHandCoins @size="20" class="tio-fg-danger" />
        </div>

        {{! Info }}
        <div class="tio-col-tight min-w-0">
          <span class="tio-text-base-medium tio-fg-primary">
            {{t "common.tas.approval.request_summary.sections.repayment"}}
          </span>

          {{! Metadata Row - Date · Label }}
          <div class="tio-row-extra-tight tio-text-caption font-medium tracking-wide">
            <span>{{@item.formattedDate}}</span>
            <span class="tio-fg-disabled">·</span>
            <span>{{@label}}</span>
          </div>
        </div>
      </div>

      {{! Right Frame - Amount & Actions }}
      <div class="tio-row-tight">
        <span class="tio-text-base-semibold tio-fg-muted tabular-nums">
          {{@item.formattedAmount}}
        </span>

        {{! Actions Menu }}
        {{#if @item.canEdit}}
          <RepaymentItemActionsMenu
            @item={{@item}}
            @application={{@application}}
            @onOpenEditModal={{@onOpenEditModal}}
            @onOpenCancelModal={{@onOpenCancelModal}}
          />
        {{else}}
          <div class="w-5"></div>
        {{/if}}
      </div>
    </div>
  </div>
</template>;

// RepaymentItemActionsMenu Component
interface RepaymentItemActionsMenuSig {
  Args: {
    item: RepaymentItem;
    application: TasApplicationModel;
    onOpenEditModal: (event: FormattedHistoryEvent, action: string) => void;
    onOpenCancelModal: (event: FormattedHistoryEvent) => void;
  };
  Element: HTMLDivElement;
}

class RepaymentItemActionsMenu extends Component<RepaymentItemActionsMenuSig> {
  @service declare intl: Intl;

  @tracked isMenuOpen = false;

  toggleMenu = () => {
    this.isMenuOpen = !this.isMenuOpen;
  };

  closeMenu = () => {
    this.isMenuOpen = false;
  };

  /**
   * Convert RepaymentItem to FormattedHistoryEvent for compatibility with existing edit modals
   */
  get eventForModal(): FormattedHistoryEvent {
    const item = this.args.item;
    return {
      id: item.id,
      eventType: item.assistanceType || 'assistance',
      eventLabel: '',
      amount: item.amount,
      formattedAmount: item.formattedAmount,
      actionTakenBy: item.approverName || null,
      date: item.date,
      formattedDate: item.formattedDate,
      formattedTime: item.formattedTime,
      canEdit: item.canEdit,
      assistanceId: item.assistanceId,
      record: item.record,
      to: item.to,
      logType: 'assistance',
      comment: item.approverComment,
    };
  }

  handleAction = (action: string) => {
    this.isMenuOpen = false;
    setTimeout(() => {
      if (action === 'delete') {
        this.args.onOpenCancelModal(this.eventForModal);
      } else {
        this.args.onOpenEditModal(this.eventForModal, action);
      }
    }, 500);
  };

  <template>
    <Popover
      @placement="left-start"
      @isOpen={{this.isMenuOpen}}
      @onClose={{this.closeMenu}}
      @maxWidth={{200}}
    >
      <:trigger as |pop|>
        <Button
          @appearance="minimal"
          @size="sm"
          {{pop.register}}
          {{on "click" this.toggleMenu}}
          data-test-repayment-actions={{@item.id}}
        >
          <PhDotsThreeVertical @size="24" />
        </Button>
      </:trigger>
      <:body>
        <div class="tio-col-flush py-1 whitespace-nowrap">
          <button
            type="button"
            class="tio-menu-action tio-menu-action--compact"
            {{on "click" (fn this.handleAction "edit")}}
            data-test-edit={{@item.id}}
          >
            <PhNotePencil @size="16" />
            {{t "common.tas.approval.request_summary.change_amount"}}
          </button>

          <button
            type="button"
            class="tio-menu-action tio-menu-action--compact"
            {{on "click" (fn this.handleAction "forgive")}}
            data-test-forgive={{@item.id}}
          >
            <PhGift @size="16" />
            {{t "common.tas.approval.request_summary.forgive"}}
          </button>

          <button
            type="button"
            class="tio-menu-action tio-menu-action--compact"
            {{on "click" (fn this.handleAction "settle")}}
            data-test-settle={{@item.id}}
          >
            <PhShieldCheck @size="16" />
            {{t "common.tas.approval.request_summary.mark_as_paid"}}
          </button>

          <hr class="my-1 tio-border-subtle" />

          <button
            type="button"
            class="tio-row-tight w-full px-3 py-2 text-sm tio-fg-danger hover:tio-bg-danger-soft rounded"
            {{on "click" (fn this.handleAction "delete")}}
            data-test-delete={{@item.id}}
          >
            <PhTrash @size="16" />
            {{t "common.tas.approval.request_summary.cancel_repayment"}}
          </button>
        </div>
      </:body>
    </Popover>
  </template>
}

// ========================================
// CompletedItemCard Component
// ========================================
interface CompletedItemCardSig {
  Args: {
    item: CompletedItem;
    label: string;
  };
  Element: HTMLDivElement;
}

const CompletedItemCard: TOC<CompletedItemCardSig> = <template>
  <div
    class="tio-assistance-status-card"
    data-test-completed-item={{@item.id}}
    role="listitem"
    aria-label={{@label}}
  >
    <div class="tio-row-between gap-8">
      {{! Left Frame - Icon and Info }}
      <div class="tio-row-loose tio-fill-min">
        {{! Icon Badge }}
        <div class="shrink-0 tio-bg-info-soft rounded-full p-1">
          <PhCheckCircle @size="20" class="tio-fg-info" />
        </div>

        {{! Info }}
        <div class="tio-col-tight min-w-0">
          {{! First line: Payment type }}
          <span class="tio-text-base-medium tio-fg-primary">
            {{#if (eq @item.manner "TAS.AssistanceManner.COURSE_CASH")}}
              {{htmlSafe (t "common.tas.course_cash.default")}}
            {{else}}
              {{t "common.progress_tracker.reimbursement"}}
            {{/if}}
          </span>

          {{! Metadata Row - Date · Label }}
          <div class="tio-row-extra-tight tio-text-caption font-medium tracking-wide">
            <span>{{@item.formattedDate}}</span>
            <span class="tio-fg-disabled">·</span>
            <span>{{@label}}</span>
          </div>
        </div>
      </div>

      {{! Right Frame - Amount }}
      <div class="tio-row-tight">
        <span class="tio-text-base-semibold tio-fg-primary tabular-nums">
          {{@item.formattedAmount}}
        </span>
        {{! Spacer for alignment with pending cards that have menu }}
        <div class="w-5"></div>
      </div>
    </div>
  </div>
</template>;
